// src/pages/VolunteerDirectory.js - Updated with CSV Upload, Export, Edit, Onboarding Checklist & COMMUNICATIONS INTEGRATION
import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Papa from 'papaparse';

const VolunteerDirectory = ({ currentUser }) => {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  
  // ========== NEW: SELECTION MODE STATES ==========
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedVolunteerIds, setSelectedVolunteerIds] = useState([]);
  const [showSaveListModal, setShowSaveListModal] = useState(false);
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [savingList, setSavingList] = useState(false);
  // =================================================
  
  // CSV Upload States
  const [csvFile, setCsvFile] = useState(null);
  const [isProcessingCSV, setIsProcessingCSV] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]);
  const [showCsvSection, setShowCsvSection] = useState(false);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    emergencyContact: '',
    emergencyPhone: '',
    skills: [],
    interests: [],
    additionalInfo: '',
    backgroundCheckStatus: 'not_started',
    status: 'active'
  });

  // Checklist states
  const [checklistConfig, setChecklistConfig] = useState([]);
  const [showChecklistConfig, setShowChecklistConfig] = useState(false);
  const [viewingChecklist, setViewingChecklist] = useState(null);
  const [uploadingFile, setUploadingFile] = useState({});

  const TEMPLATE_FILE_NAME = "volunteer_template_v1.csv";

  // Default checklist items - can be customized per organization
  const defaultChecklistItems = [
    { id: 'background_check', label: 'Background Check Completed', order: 1 },
    { id: 'orientation_attended', label: 'Orientation Session Attended', order: 2 },
    { id: 'training_completed', label: 'Required Training Completed', order: 3 },
    { id: 'id_verified', label: 'Identity Verification', order: 4 },
    { id: 'emergency_form', label: 'Emergency Contact Form', order: 5 },
    { id: 'handbook_received', label: 'Volunteer Handbook Received', order: 6 },
    { id: 'photo_taken', label: 'Photo ID Badge Created', order: 7 },
    { id: 'references_checked', label: 'References Checked', order: 8 }
  ];

  // Default options (used as fallback if no configuration exists)
  const defaultSkillOptions = [
    'Childcare', 'Youth Ministry', 'Music/Worship', 'Teaching', 'Administration',
    'Event Setup', 'Food Service', 'Technology', 'Cleaning', 'Greeting/Hospitality',
    'Security', 'Transportation', 'Photography', 'Audio/Visual', 'Maintenance'
  ];

  const defaultInterestOptions = [
    'Children\'s Ministry', 'Youth Programs', 'Adult Education', 'Community Outreach',
    'Worship Services', 'Special Events', 'Missions', 'Small Groups',
    'Facilities', 'Administration', 'Technology', 'Pastoral Care'
  ];

  // State for dynamic skill and interest options from configuration
  const [skillOptions, setSkillOptions] = useState(defaultSkillOptions);
  const [interestOptions, setInterestOptions] = useState(defaultInterestOptions);

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadVolunteers();
      loadFormConfiguration();
      loadChecklistConfig();
    }
  }, [currentUser, statusFilter]);

  // ========== NEW: SELECTION MODE FUNCTIONS ==========
  const handleToggleSelectionMode = () => {
    setIsSelecting(!isSelecting);
    setSelectedVolunteerIds([]);
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredVolunteers.map(v => v.id);
    setSelectedVolunteerIds(allFilteredIds);
  };

  const handleDeselectAll = () => {
    setSelectedVolunteerIds([]);
  };

  const toggleVolunteerSelection = (volunteerId) => {
    setSelectedVolunteerIds(prev => {
      if (prev.includes(volunteerId)) {
        return prev.filter(id => id !== volunteerId);
      } else {
        return [...prev, volunteerId];
      }
    });
  };

  const handleSaveAsList = async () => {
    if (!listName.trim()) {
      alert('Please enter a list name');
      return;
    }

    if (selectedVolunteerIds.length === 0) {
      alert('Please select at least one volunteer');
      return;
    }

    try {
      setSavingList(true);
      
      const listData = {
        name: listName.trim(),
        description: listDescription.trim(),
        volunteerIds: selectedVolunteerIds,
        volunteerCount: selectedVolunteerIds.length,
        filters: {
          skills: skillFilter ? [skillFilter] : [],
          status: statusFilter === 'all' ? null : statusFilter,
          searchTerm: searchTerm || null
        },
        tenantId: currentUser.tenantId,
        createdAt: serverTimestamp(),
        createdBy: currentUser.email,
        useCount: 0,
        lastUsed: null
      };
      
      await addDoc(collection(db, 'volunteerLists'), listData);
      
      alert(`List "${listName}" saved successfully!`);
      setShowSaveListModal(false);
      setListName('');
      setListDescription('');
      setIsSelecting(false);
      setSelectedVolunteerIds([]);
      
    } catch (err) {
      console.error('Error saving list:', err);
      alert('Failed to save list. Please try again.');
    } finally {
      setSavingList(false);
    }
  };

  const handleSendCommunication = () => {
    if (selectedVolunteerIds.length === 0) {
      alert('Please select at least one volunteer');
      return;
    }
    
    // Store the selected IDs in sessionStorage to pass to Communications page
    sessionStorage.setItem('selectedVolunteerIds', JSON.stringify(selectedVolunteerIds));
    sessionStorage.setItem('selectedVolunteerCount', selectedVolunteerIds.length);
    
    // Navigate to Communications page
    window.location.href = '/communications';
  };
  // ====================================================

  // Load checklist configuration
  const loadChecklistConfig = async () => {
    try {
      const configDoc = await getDoc(
        doc(db, 'tenantConfigurations', `${currentUser.tenantId}_volunteer_checklist`)
      );
      
      if (configDoc.exists()) {
        setChecklistConfig(configDoc.data().items || defaultChecklistItems);
      } else {
        setChecklistConfig(defaultChecklistItems);
      }
    } catch (err) {
      console.error('Error loading checklist config:', err);
      setChecklistConfig(defaultChecklistItems);
    }
  };

  // Save checklist configuration
  const saveChecklistConfig = async () => {
    try {
      await setDoc(
        doc(db, 'tenantConfigurations', `${currentUser.tenantId}_volunteer_checklist`),
        {
          items: checklistConfig,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
          tenantId: currentUser.tenantId
        }
      );
      alert('Checklist configuration saved successfully!');
      setShowChecklistConfig(false);
    } catch (err) {
      console.error('Error saving checklist config:', err);
      alert('Failed to save checklist configuration');
    }
  };

  // Add new checklist item
  const addChecklistItem = () => {
    const newItem = {
      id: `custom_${Date.now()}`,
      label: 'New Checklist Item',
      order: checklistConfig.length + 1
    };
    setChecklistConfig([...checklistConfig, newItem]);
  };

  // Update checklist config item
  const updateChecklistConfigItem = (index, field, value) => {
    setChecklistConfig(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Remove checklist config item
  const removeChecklistConfigItem = (index) => {
    setChecklistConfig(prev => prev.filter((_, i) => i !== index));
  };

  // Update checklist item for a volunteer
  const updateChecklistItem = async (volunteerId, itemId, field, value) => {
    try {
      const volunteer = volunteers.find(v => v.id === volunteerId);
      const currentChecklist = volunteer.onboardingChecklist || {};
      
      if (!currentChecklist[itemId]) {
        currentChecklist[itemId] = {
          completed: false,
          notes: '',
          fileUrl: '',
          fileName: '',
          updatedAt: null,
          updatedBy: null
        };
      }
      
      currentChecklist[itemId] = {
        ...currentChecklist[itemId],
        [field]: value,
        updatedAt: new Date(),
        updatedBy: currentUser.email
      };
      
      await updateDoc(doc(db, 'volunteers', volunteerId), {
        onboardingChecklist: currentChecklist
      });
      
      // Update local state
      setVolunteers(prev => prev.map(vol => 
        vol.id === volunteerId 
          ? { ...vol, onboardingChecklist: currentChecklist }
          : vol
      ));
      
    } catch (err) {
      console.error('Error updating checklist:', err);
      setError(`Failed to update checklist: ${err.message}`);
    }
  };

  // Handle file upload for checklist
  const handleChecklistFileUpload = async (volunteerId, itemId, file) => {
    if (!file) return;
    
    try {
      setUploadingFile(prev => ({ ...prev, [`${volunteerId}-${itemId}`]: true }));
      
      // Create a reference to the file in Firebase Storage
      const fileName = `volunteers/${volunteerId}/${itemId}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update the checklist with file info
      await updateChecklistItem(volunteerId, itemId, 'fileUrl', downloadURL);
      await updateChecklistItem(volunteerId, itemId, 'fileName', file.name);
      
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(`Failed to upload file: ${err.message}`);
    } finally {
      setUploadingFile(prev => ({ ...prev, [`${volunteerId}-${itemId}`]: false }));
    }
  };

  // Calculate checklist progress
  const calculateChecklistProgress = (checklist) => {
    if (!checklist || Object.keys(checklist).length === 0) return 0;
    
    const totalItems = checklistConfig.length;
    const completedItems = Object.values(checklist).filter(item => item.completed).length;
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  // Load form configuration to get customized skills and interests
  const loadFormConfiguration = async () => {
    if (!currentUser?.tenantId) return;
    
    try {
      const configDoc = await getDoc(
        doc(db, 'tenantConfigurations', `${currentUser.tenantId}_volunteer_registration`)
      );
      
      if (configDoc.exists()) {
        const config = configDoc.data().configValue;
        
        // Load skills options if they exist in config
        if (config?.sections?.skills?.fields?.skills?.options) {
          setSkillOptions(config.sections.skills.fields.skills.options);
        }
        
        // Load interests options if they exist in config
        if (config?.sections?.interests?.fields?.interests?.options) {
          setInterestOptions(config.sections.interests.fields.interests.options);
        }
      }
    } catch (error) {
      console.error('Error loading form configuration:', error);
      setSkillOptions(defaultSkillOptions);
      setInterestOptions(defaultInterestOptions);
    }
  };

  const loadVolunteers = async () => {
    try {
      setLoading(true);
      
      let q;
      if (statusFilter === 'all') {
        q = query(
          collection(db, 'volunteers'),
          where('tenantId', '==', currentUser.tenantId),
          orderBy('lastName', 'asc')
        );
      } else {
        q = query(
          collection(db, 'volunteers'),
          where('tenantId', '==', currentUser.tenantId),
          where('status', '==', statusFilter),
          orderBy('lastName', 'asc')
        );
      }
      
      const snapshot = await getDocs(q);
      const volunteersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Initialize checklist if it doesn't exist
        onboardingChecklist: doc.data().onboardingChecklist || {}
      }));
      
      setVolunteers(volunteersData);
      
    } catch (err) {
      console.error('Error loading volunteers:', err);
      setError('Failed to load volunteers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // CSV Upload Functions
  const handleFileChange = (event) => {
    setCsvFile(event.target.files[0]);
    setUploadStatus('');
    setProcessedCount(0);
    setErrorCount(0);
    setErrorMessages([]);
  };

  const handleDownloadTemplate = () => {
    const csvHeader = "firstName,lastName,email,phone,address,city,state,zip,emergencyContact,emergencyPhone,backgroundCheckStatus,status,additionalInfo\n";
    const csvExample1 = "John,Doe,john.doe@example.com,555-1234,123 Main St,Houston,TX,77001,Jane Doe,555-5678,not_started,active,Interested in youth ministry\n";
    const csvExample2 = "Mary,Smith,mary.smith@example.com,555-9876,456 Oak Ave,Houston,TX,77002,Bob Smith,555-4321,pending,active,Available weekends only\n";
    const csvContent = csvHeader + csvExample1 + csvExample2;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', TEMPLATE_FILE_NAME);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  // CSV Export Function
  const handleExportCSV = () => {
    try {
      // Prepare data for export
      const exportData = filteredVolunteers.map(volunteer => ({
        firstName: volunteer.firstName || '',
        lastName: volunteer.lastName || '',
        email: volunteer.email || '',
        phone: volunteer.phone || '',
        address: volunteer.address || '',
        city: volunteer.city || '',
        state: volunteer.state || '',
        zip: volunteer.zip || '',
        emergencyContact: volunteer.emergencyContact || '',
        emergencyPhone: volunteer.emergencyPhone || '',
        skills: (volunteer.skills || []).join('; '),
        interests: (volunteer.interests || []).join('; '),
        backgroundCheckStatus: volunteer.backgroundCheckStatus || 'not_started',
        status: volunteer.status || 'active',
        totalHours: volunteer.totalHours || 0,
        joinedDate: volunteer.joinedAt ? formatDate(volunteer.joinedAt) : '',
        checklistProgress: calculateChecklistProgress(volunteer.onboardingChecklist) + '%',
        additionalInfo: volunteer.additionalInfo || ''
      }));

      // Convert to CSV using Papa Parse
      const csv = Papa.unparse(exportData);
      
      // Create and download the file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `volunteers_${currentUser.tenantId}_${timestamp}.csv`;
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Show success message
      alert(`Successfully exported ${exportData.length} volunteer(s) to ${filename}`);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export volunteers. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!csvFile) {
      alert("Please select a CSV file.");
      return;
    }
    
    setIsProcessingCSV(true);
    setUploadStatus('Preparing for upload...');
    setErrorMessages([]);
    setErrorCount(0);
    setProcessedCount(0);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let localProcessed = 0;
        let localErrors = 0;
        const localErrorMsgsArr = [];
        
        const dataRows = results.data;
        if (results.errors.length) {
            results.errors.forEach(err => localErrorMsgsArr.push(`CSV Row Parsing Error: ${err.message} (Row: ${err.row + 2})`));
            localErrors += results.errors.length;
        }

        setUploadStatus(`CSV parsed. Found ${dataRows.length} volunteers. Writing to database...`);
        const batchSize = 400;

        for (let i = 0; i < dataRows.length; i += batchSize) {
          const batch = writeBatch(db);
          const chunk = dataRows.slice(i, i + batchSize);
          let validRowsInChunk = 0;

          for (const row of chunk) {
            const firstName = row.firstName?.trim();
            const lastName = row.lastName?.trim();
            const email = row.email?.trim().toLowerCase();
            const phone = row.phone?.trim();
            const status = row.status?.trim().toLowerCase() || 'active';

            // Check for missing required fields
            if (!firstName || !lastName || !email) {
              const missing = ["firstName", "lastName", "email"].filter(f => !row[f]?.trim()).join(", ");
              localErrorMsgsArr.push(`Row Skipped (${firstName || 'N/A'} ${lastName || 'N/A'}): Missing required fields: ${missing}.`);
              localErrors++;
              continue;
            }

            // Validate status values
            const validStatuses = ['active', 'inactive', 'pending'];
            if (!validStatuses.includes(status)) {
              localErrorMsgsArr.push(`Row Skipped (${firstName} ${lastName}): Invalid status '${status}'. Must be one of: ${validStatuses.join(', ')}.`);
              localErrors++;
              continue;
            }

            // Validate background check status
            const bgCheckStatus = row.backgroundCheckStatus?.trim().toLowerCase() || 'not_started';
            const validBgStatuses = ['not_started', 'pending', 'cleared', 'denied'];
            if (!validBgStatuses.includes(bgCheckStatus.replace(' ', '_'))) {
              localErrorMsgsArr.push(`Row Skipped (${firstName} ${lastName}): Invalid background check status '${bgCheckStatus}'. Must be one of: ${validBgStatuses.join(', ')}.`);
              localErrors++;
              continue;
            }

            // Check if volunteer with this email already exists
            const existingVolunteerQuery = query(
              collection(db, 'volunteers'),
              where('tenantId', '==', currentUser.tenantId),
              where('email', '==', email)
            );
            const existingSnapshot = await getDocs(existingVolunteerQuery);
            
            let docRef;
            let volunteerData;
            
            if (!existingSnapshot.empty) {
              // Update existing volunteer
              docRef = existingSnapshot.docs[0].ref;
              volunteerData = {
                firstName,
                lastName,
                email,
                phone: phone || '',
                address: row.address?.trim() || '',
                city: row.city?.trim() || '',
                state: row.state?.trim() || '',
                zip: row.zip?.trim() || '',
                emergencyContact: row.emergencyContact?.trim() || '',
                emergencyPhone: row.emergencyPhone?.trim() || '',
                backgroundCheckStatus: bgCheckStatus.replace(' ', '_'),
                status,
                additionalInfo: row.additionalInfo?.trim() || '',
                updatedAt: serverTimestamp(),
              };
              batch.update(docRef, volunteerData);
            } else {
              // Create new volunteer with empty checklist
              volunteerData = {
                firstName,
                lastName,
                email,
                phone: phone || '',
                address: row.address?.trim() || '',
                city: row.city?.trim() || '',
                state: row.state?.trim() || '',
                zip: row.zip?.trim() || '',
                emergencyContact: row.emergencyContact?.trim() || '',
                emergencyPhone: row.emergencyPhone?.trim() || '',
                skills: [],
                interests: [],
                backgroundCheckStatus: bgCheckStatus.replace(' ', '_'),
                status,
                additionalInfo: row.additionalInfo?.trim() || '',
                tenantId: currentUser.tenantId,
                joinedAt: serverTimestamp(),
                createdAt: serverTimestamp(),
                createdBy: currentUser.uid,
                totalHours: 0,
                registrationMethod: 'csv_import',
                onboardingChecklist: {} // Initialize empty checklist
              };
              
              const newVolunteerRef = doc(collection(db, 'volunteers'));
              batch.set(newVolunteerRef, volunteerData);
            }
            
            localProcessed++;
            validRowsInChunk++;
          }

          try {
            if (validRowsInChunk > 0) {
              await batch.commit();
            }
          } catch (batchError) {
            console.error(`Error committing batch:`, batchError);
            localErrorMsgsArr.push(`Critical Error committing batch: ${batchError.message}`);
            localErrors += validRowsInChunk;
            localProcessed -= validRowsInChunk;
          }
        }

        setProcessedCount(localProcessed);
        setErrorCount(localErrors);
        setErrorMessages(localErrorMsgsArr);
        
        if (localErrors > 0) {
          setUploadStatus(`Upload finished. Processed: ${localProcessed}. Errors: ${localErrors}.`);
        } else {
          setUploadStatus(`Upload successful! ${localProcessed} volunteers created/updated.`);
        }
        
        setIsProcessingCSV(false);
        setCsvFile(null);
        loadVolunteers();
      },
      error: (err) => {
        console.error("PapaParse File Reading Error:", err);
        setUploadStatus(`Error parsing CSV: ${err.message}`);
        setIsProcessingCSV(false);
      }
    });
  };

  const openAddModal = () => {
    setEditingVolunteer(null);
    setVolunteerForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      emergencyContact: '',
      emergencyPhone: '',
      skills: [],
      interests: [],
      additionalInfo: '',
      backgroundCheckStatus: 'not_started',
      status: 'active'
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (volunteer) => {
    setEditingVolunteer(volunteer);
    setVolunteerForm({
      firstName: volunteer.firstName || '',
      lastName: volunteer.lastName || '',
      email: volunteer.email || '',
      phone: volunteer.phone || '',
      address: volunteer.address || '',
      city: volunteer.city || '',
      state: volunteer.state || '',
      zip: volunteer.zip || '',
      emergencyContact: volunteer.emergencyContact || '',
      emergencyPhone: volunteer.emergencyPhone || '',
      skills: volunteer.skills || [],
      interests: volunteer.interests || [],
      additionalInfo: volunteer.additionalInfo || '',
      backgroundCheckStatus: volunteer.backgroundCheckStatus || 'not_started',
      status: volunteer.status || 'active'
    });
    setShowModal(true);
    setError('');
  };

  const handleSaveVolunteer = async () => {
    try {
      setSaving(true);
      setError('');

      // Basic validation
      if (!volunteerForm.firstName || !volunteerForm.lastName || !volunteerForm.email) {
        setError('Please fill in first name, last name, and email.');
        return;
      }

      // Check if email already exists (but allow current volunteer's email when editing)
      const existingVolunteerQuery = query(
        collection(db, 'volunteers'),
        where('tenantId', '==', currentUser.tenantId),
        where('email', '==', volunteerForm.email.toLowerCase())
      );
      const existingSnapshot = await getDocs(existingVolunteerQuery);
      
      if (!existingSnapshot.empty) {
        const existingDoc = existingSnapshot.docs[0];
        // Allow if this is the same volunteer we're editing
        if (!editingVolunteer || existingDoc.id !== editingVolunteer.id) {
          setError('A volunteer with this email address already exists.');
          return;
        }
      }

      if (editingVolunteer) {
        // Update existing volunteer (preserve checklist)
        const volunteerData = {
          ...volunteerForm,
          email: volunteerForm.email.toLowerCase(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.uid
        };

        await updateDoc(doc(db, 'volunteers', editingVolunteer.id), volunteerData);
        console.log('Volunteer updated:', editingVolunteer.id);
        
      } else {
        // Create new volunteer with empty checklist
        const volunteerData = {
          ...volunteerForm,
          email: volunteerForm.email.toLowerCase(),
          tenantId: currentUser.tenantId,
          joinedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
          totalHours: 0,
          registrationMethod: 'manual',
          onboardingChecklist: {} // Initialize empty checklist
        };

        const docRef = await addDoc(collection(db, 'volunteers'), volunteerData);
        console.log('New volunteer added with ID:', docRef.id);
      }
      
      // Close modal and reload data
      setShowModal(false);
      setEditingVolunteer(null);
      await loadVolunteers();
      
    } catch (err) {
      console.error('Error saving volunteer:', err);
      setError('Failed to save volunteer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateVolunteerField = (field, value) => {
    setVolunteerForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleSkill = (skill) => {
    setVolunteerForm(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const toggleInterest = (interest) => {
    setVolunteerForm(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getAvailabilityText = (availability) => {
    if (!availability) return 'Not specified';
    
    const availableTimes = [];
    Object.entries(availability).forEach(([day, times]) => {
      const dayTimes = Object.entries(times)
        .filter(([time, available]) => available)
        .map(([time]) => time);
      
      if (dayTimes.length > 0) {
        availableTimes.push(`${day.charAt(0).toUpperCase() + day.slice(1)}: ${dayTimes.join(', ')}`);
      }
    });
    
    return availableTimes.length > 0 ? availableTimes.join(' | ') : 'No availability set';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'inactive': return '#6c757d';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const filteredVolunteers = volunteers.filter(volunteer => {
    const matchesSearch = !searchTerm || 
      `${volunteer.firstName} ${volunteer.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      volunteer.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSkill = !skillFilter || 
      (volunteer.skills && volunteer.skills.includes(skillFilter));
    
    return matchesSearch && matchesSkill;
  });

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading volunteer directory...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            Volunteer Directory
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
            Browse and manage your volunteer database
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {/* Communications Button */}
          <button
            onClick={() => window.location.href = '/communications'}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>✉️</span>
            Communications
          </button>
          {/* ========== NEW: SELECTION MODE BUTTON ========== */}
          <button
            onClick={handleToggleSelectionMode}
            style={{
              backgroundColor: isSelecting ? '#dc3545' : '#6f42c1',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>{isSelecting ? '✖️' : '☑️'}</span>
            {isSelecting ? 'Cancel Selection' : 'Select Volunteers'}
          </button>
          {/* ================================================ */}
          
          <button
            onClick={() => setShowChecklistConfig(!showChecklistConfig)}
            style={{
              backgroundColor: '#6f42c1',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>⚙️</span>
            Configure Checklist
          </button>
          <button
            onClick={() => setShowCsvSection(!showCsvSection)}
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{ fontSize: '18px' }}>📤</span>
            {showCsvSection ? 'Hide' : 'Show'} CSV Tools
          </button>
          <button
            onClick={openAddModal}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{ fontSize: '18px' }}>👤</span>
            Add New Volunteer
          </button>
        </div>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* ========== NEW: SELECTION MODE TOOLBAR ========== */}
      {isSelecting && (
        <div style={{
          position: 'sticky',
          top: '0',
          backgroundColor: '#fff',
          padding: '15px',
          borderRadius: '8px',
          border: '2px solid #6f42c1',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#6f42c1' }}>
              {selectedVolunteerIds.length} of {filteredVolunteers.length} selected
            </span>
            <button
              onClick={handleSelectAll}
              style={{
                backgroundColor: 'transparent',
                color: '#6f42c1',
                border: '1px solid #6f42c1',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              style={{
                backgroundColor: 'transparent',
                color: '#6f42c1',
                border: '1px solid #6f42c1',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowSaveListModal(true)}
              disabled={selectedVolunteerIds.length === 0}
              style={{
                backgroundColor: selectedVolunteerIds.length > 0 ? '#28a745' : '#ccc',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: selectedVolunteerIds.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              💾 Save as List
            </button>
            <button
              onClick={handleSendCommunication}
              disabled={selectedVolunteerIds.length === 0}
              style={{
                backgroundColor: selectedVolunteerIds.length > 0 ? '#007bff' : '#ccc',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: selectedVolunteerIds.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              ✉️ Send Communication
            </button>
          </div>
        </div>
      )}
      {/* ================================================ */}

      {/* Checklist Configuration Modal */}
      {showChecklistConfig && (
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #6f42c1',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#6f42c1' }}>Configure Onboarding Checklist</h3>
          
          <div style={{ marginBottom: '15px' }}>
            {checklistConfig.map((item, index) => (
              <div key={item.id} style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 80px',
                gap: '10px',
                alignItems: 'center',
                marginBottom: '10px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <input
                  type="number"
                  value={item.order}
                  onChange={(e) => updateChecklistConfigItem(index, 'order', parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => updateChecklistConfigItem(index, 'label', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <button
                  onClick={() => removeChecklistConfigItem(index)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={addChecklistItem}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Add Item
            </button>
            <button
              onClick={saveChecklistConfig}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Save Configuration
            </button>
            <button
              onClick={() => setShowChecklistConfig(false)}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CSV Upload/Export Section */}
      {showCsvSection && (
        <section style={{ marginBottom: '30px' }}>
          {/* CSV Export */}
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ backgroundColor: '#d4edda', padding: '20px', borderBottom: '1px solid #ddd' }}>
              <h2 style={{ margin: 0, color: '#155724', display: 'flex', alignItems: 'center', fontSize: '24px' }}>
                📥 Export Volunteers
              </h2>
              <p style={{ margin: '8px 0 0 0', color: '#155724' }}>
                Download current volunteer data as a CSV file for reporting or offline use.
              </p>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                  The export will include all volunteers matching your current filters ({filteredVolunteers.length} volunteer{filteredVolunteers.length !== 1 ? 's' : ''}).
                </p>
                <p style={{ fontSize: '12px', color: '#999' }}>
                  Exported fields: Name, Email, Phone, Address, Emergency Contact, Skills, Interests, Status, Background Check Status, Total Hours, Join Date, Checklist Progress
                </p>
              </div>
              <button 
                onClick={handleExportCSV}
                disabled={filteredVolunteers.length === 0}
                style={{ 
                  padding: '12px 24px', 
                  backgroundColor: filteredVolunteers.length > 0 ? '#28a745' : '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: filteredVolunteers.length > 0 ? 'pointer' : 'not-allowed', 
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                <span>📥</span> Export {filteredVolunteers.length} Volunteer{filteredVolunteers.length !== 1 ? 's' : ''} to CSV
              </button>
            </div>
          </div>

          {/* CSV Import */}
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#e3f2fd', padding: '20px', borderBottom: '1px solid #ddd' }}>
              <h2 style={{ margin: 0, color: '#1976d2', display: 'flex', alignItems: 'center', fontSize: '24px' }}>
                📤 Import Volunteers
              </h2>
              <p style={{ margin: '8px 0 0 0', color: '#666' }}>
                Import multiple volunteers at once using a CSV file. Download the template to get started.
              </p>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <button type="button" onClick={handleDownloadTemplate} 
                        style={{ padding: '10px 16px', marginRight: '15px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} 
                        disabled={isProcessingCSV}>
                  📥 Download CSV Template
                </button>
                <span style={{fontSize: '14px', color: '#666'}}>({TEMPLATE_FILE_NAME})</span>
              </div>
              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                <p style={{marginTop: 0, fontSize: '0.9em', color: '#555'}}>
                  Required: <strong>firstName, lastName, email</strong>.<br/>
                  Optional: <strong>phone, address, city, state, zip, emergencyContact, emergencyPhone, backgroundCheckStatus, status, additionalInfo</strong>.<br/>
                  Status values: active, inactive, pending.<br/>
                  Background check values: not_started, pending, cleared, denied.<br/>
                  Note: Skills and interests can be added later through the edit feature.
                </p>
              </div>
              <input type="file" accept=".csv" onChange={handleFileChange} disabled={isProcessingCSV} 
                     style={{marginBottom: '15px', display: 'block', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '300px'}}/>
              <button onClick={handleUpload} disabled={isProcessingCSV || !csvFile} 
                      style={{ padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                {isProcessingCSV ? 'Uploading...' : '⬆️ Upload & Process CSV'}
              </button>
              {uploadStatus && (
                <div style={{ marginTop: '20px', padding: '15px', borderRadius: '4px', 
                            backgroundColor: (errorCount > 0 || uploadStatus.toLowerCase().includes('error')) ? '#f8d7da' : '#d4edda',
                            color: (errorCount > 0 || uploadStatus.toLowerCase().includes('error')) ? '#721c24' : '#155724',
                            border: `1px solid ${(errorCount > 0 || uploadStatus.toLowerCase().includes('error')) ? '#f5c6cb' : '#c3e6cb'}` }}>
                  <div dangerouslySetInnerHTML={{ __html: uploadStatus }} />
                </div>
              )}
              {errorCount > 0 && errorMessages.length > 0 && (
                <div style={{ marginTop: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '15px' }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: '500', color: '#721c24' }}>Error Details (showing first 10):</p>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#721c24' }}>
                    {errorMessages.slice(0,10).map((msg,idx) => <li key={idx} style={{fontSize:'13px', marginBottom: '5px'}}>{msg}</li>)}
                    {errorMessages.length > 10 && <li style={{fontStyle: 'italic'}}>...and {errorMessages.length - 10} more.</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px', 
        border: '1px solid #dee2e6',
        marginBottom: '20px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '20px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Search Volunteers
            </label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Filter by Skill
            </label>
            <select
              value={skillFilter}
              onChange={(e) => setSkillFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              <option value="">All Skills</option>
              {skillOptions.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px'
              }}
            >
              <option value="active">Active ({volunteers.filter(v => v.status === 'active').length})</option>
              <option value="inactive">Inactive ({volunteers.filter(v => v.status === 'inactive').length})</option>
              <option value="pending">Pending ({volunteers.filter(v => v.status === 'pending').length})</option>
              <option value="all">All ({volunteers.length})</option>
            </select>
          </div>

          <div>
            <button
              onClick={() => {
                setSearchTerm('');
                setSkillFilter('');
                setStatusFilter('active');
              }}
              style={{
                width: '100%',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ marginBottom: '20px', color: '#666' }}>
        Showing {filteredVolunteers.length} of {volunteers.length} volunteers
      </div>

      {/* Volunteers Grid */}
      {filteredVolunteers.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: 'white', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>👥</div>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            No volunteers found
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            {searchTerm || skillFilter 
              ? 'Try adjusting your search filters.'
              : 'No volunteers have been approved yet.'
            }
          </div>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: '20px' 
        }}>
          {filteredVolunteers.map(volunteer => {
            const checklistProgress = calculateChecklistProgress(volunteer.onboardingChecklist);
            const isSelected = selectedVolunteerIds.includes(volunteer.id);
            
            return (
              <div 
                key={volunteer.id} 
                style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px', 
                  border: isSelected ? '3px solid #6f42c1' : '1px solid #dee2e6',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onClick={() => {
                  if (isSelecting) {
                    toggleVolunteerSelection(volunteer.id);
                  } else {
                    setSelectedVolunteer(selectedVolunteer === volunteer.id ? null : volunteer.id);
                  }
                }}
              >
                {/* ========== NEW: SELECTION CHECKBOX ========== */}
                {isSelecting && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: isSelected ? '#6f42c1' : 'white',
                    border: isSelected ? 'none' : '2px solid #6f42c1',
                    borderRadius: '4px',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isSelected && (
                      <span style={{ color: 'white', fontSize: '16px' }}>✓</span>
                    )}
                  </div>
                )}
                {/* ============================================= */}

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                      {volunteer.firstName} {volunteer.lastName}
                      {volunteer.registrationMethod === 'csv_import' && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '12px', 
                          color: '#17a2b8',
                          fontWeight: 'normal'
                        }}>
                          (CSV Import)
                        </span>
                      )}
                      {volunteer.registrationMethod === 'manual' && (
                        <span style={{ 
                          marginLeft: '8px', 
                          fontSize: '12px', 
                          color: '#6c757d',
                          fontWeight: 'normal'
                        }}>
                          (Added manually)
                        </span>
                      )}
                    </h3>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {volunteer.email}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    backgroundColor: getStatusColor(volunteer.status),
                    color: 'white',
                    textTransform: 'capitalize'
                  }}>
                    {volunteer.status}
                  </span>
                </div>

                {/* Basic Info */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                    📞 {volunteer.phone || 'No phone provided'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                    📅 Joined {formatDate(volunteer.joinedAt)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    ⏱️ {volunteer.totalHours || 0} hours served
                  </div>
                </div>

                {/* Onboarding Progress Bar */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                    <strong style={{ fontSize: '12px', color: '#666' }}>Onboarding Progress:</strong>
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                      {checklistProgress}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${checklistProgress}%`,
                      height: '100%',
                      backgroundColor: checklistProgress === 100 ? '#28a745' : '#17a2b8',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                {/* Skills */}
                {volunteer.skills && volunteer.skills.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#555', marginBottom: '6px' }}>
                      SKILLS
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {volunteer.skills.slice(0, 3).map(skill => (
                        <span 
                          key={skill}
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '12px',
                            fontSize: '11px',
                            color: '#495057'
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                      {volunteer.skills.length > 3 && (
                        <span style={{
                          padding: '2px 6px',
                          backgroundColor: '#6c757d',
                          borderRadius: '12px',
                          fontSize: '11px',
                          color: 'white'
                        }}>
                          +{volunteer.skills.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Expanded Details */}
                {selectedVolunteer === volunteer.id && !isSelecting && (
                  <div style={{ 
                    borderTop: '1px solid #dee2e6', 
                    paddingTop: '16px', 
                    marginTop: '16px',
                    fontSize: '14px'
                  }}>
                    {/* Full Address */}
                    {volunteer.address && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong>Address:</strong><br />
                        {volunteer.address}<br />
                        {volunteer.city}, {volunteer.state} {volunteer.zip}
                      </div>
                    )}

                    {/* Emergency Contact */}
                    {volunteer.emergencyContact && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong>Emergency Contact:</strong><br />
                        {volunteer.emergencyContact} - {volunteer.emergencyPhone}
                      </div>
                    )}

                    {/* All Skills */}
                    {volunteer.skills && volunteer.skills.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong>All Skills:</strong><br />
                        {volunteer.skills.join(', ')}
                      </div>
                    )}

                    {/* Interests */}
                    {volunteer.interests && volunteer.interests.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong>Interests:</strong><br />
                        {volunteer.interests.join(', ')}
                      </div>
                    )}

                    {/* Availability */}
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Availability:</strong><br />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {getAvailabilityText(volunteer.availability)}
                      </div>
                    </div>

                    {/* Background Check Status */}
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Background Check:</strong>{' '}
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        backgroundColor: volunteer.backgroundCheckStatus === 'cleared' ? '#d4edda' : 
                                       volunteer.backgroundCheckStatus === 'pending' ? '#fff3cd' : '#f8d7da',
                        color: volunteer.backgroundCheckStatus === 'cleared' ? '#155724' : 
                               volunteer.backgroundCheckStatus === 'pending' ? '#856404' : '#721c24'
                      }}>
                        {volunteer.backgroundCheckStatus || 'Not Started'}
                      </span>
                    </div>

                    {/* Additional Info */}
                    {volunteer.additionalInfo && (
                      <div style={{ marginBottom: '12px' }}>
                        <strong>Additional Info:</strong><br />
                        <div style={{ 
                          padding: '8px', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#555',
                          marginTop: '4px'
                        }}>
                          {volunteer.additionalInfo}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                      {viewingChecklist === volunteer.id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingChecklist(null);
                          }}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          Hide Checklist
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingChecklist(volunteer.id);
                          }}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          ✔️ Checklist
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(volunteer);
                        }}
                        style={{
                          backgroundColor: '#ffc107',
                          color: '#212529',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        Edit
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `mailto:${volunteer.email}`;
                        }}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Email
                      </button>
                      
                      {volunteer.phone && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `tel:${volunteer.phone}`;
                          }}
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Call
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Onboarding Checklist */}
                {viewingChecklist === volunteer.id && (
                  <div style={{
                    borderTop: '2px solid #17a2b8',
                    paddingTop: '20px',
                    marginTop: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#17a2b8' }}>
                      📋 Onboarding Checklist
                    </h4>
                    
                    {checklistConfig.sort((a, b) => a.order - b.order).map(item => {
                      const checklistData = volunteer.onboardingChecklist?.[item.id] || {};
                      const isUploading = uploadingFile[`${volunteer.id}-${item.id}`];
                      
                      return (
                        <div key={item.id} style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          padding: '15px',
                          marginBottom: '15px',
                          border: checklistData.completed ? '2px solid #28a745' : '1px solid #dee2e6'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={checklistData.completed || false}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateChecklistItem(volunteer.id, item.id, 'completed', e.target.checked);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                marginTop: '2px'
                              }}
                            />
                            
                            {/* Content */}
                            <div style={{ flex: 1 }}>
                              <label style={{
                                fontSize: '14px',
                                fontWeight: checklistData.completed ? 'bold' : 'normal',
                                color: checklistData.completed ? '#28a745' : '#333',
                                textDecoration: checklistData.completed ? 'line-through' : 'none',
                                cursor: 'pointer'
                              }}>
                                {item.label}
                              </label>
                              
                              {/* File Upload */}
                              <div style={{ marginTop: '10px' }}>
                                {checklistData.fileUrl ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', color: '#28a745' }}>
                                      ✓ File: {checklistData.fileName}
                                    </span>
                                    <a 
                                      href={checklistData.fileUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ 
                                        color: '#007bff', 
                                        textDecoration: 'underline',
                                        fontSize: '12px'
                                      }}
                                    >
                                      View
                                    </a>
                                  </div>
                                ) : (
                                  <input
                                    type="file"
                                    disabled={isUploading}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      if (e.target.files[0]) {
                                        handleChecklistFileUpload(volunteer.id, item.id, e.target.files[0]);
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ fontSize: '12px' }}
                                  />
                                )}
                                {isUploading && (
                                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                                    Uploading...
                                  </span>
                                )}
                              </div>
                              
                              {/* Notes */}
                              <div style={{ marginTop: '10px' }}>
                                <textarea
                                  placeholder="Add notes..."
                                  value={checklistData.notes || ''}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    updateChecklistItem(volunteer.id, item.id, 'notes', e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                    minHeight: '50px',
                                    padding: '6px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    resize: 'vertical'
                                  }}
                                />
                              </div>
                              
                              {/* Last Updated */}
                              {checklistData.updatedAt && (
                                <div style={{ 
                                  marginTop: '8px', 
                                  fontSize: '11px', 
                                  color: '#666',
                                  fontStyle: 'italic'
                                }}>
                                  Last updated: {formatDate(checklistData.updatedAt)} by {checklistData.updatedBy}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Click to expand hint */}
                {!isSelecting && (
                  <div style={{ 
                    textAlign: 'center', 
                    fontSize: '12px', 
                    color: '#999', 
                    marginTop: '12px',
                    borderTop: selectedVolunteer === volunteer.id ? 'none' : '1px solid #f0f0f0',
                    paddingTop: selectedVolunteer === volunteer.id ? '0' : '8px'
                  }}>
                    {selectedVolunteer === volunteer.id ? 'Click to collapse' : 'Click to view details'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {volunteers.length > 0 && (
        <div style={{ 
          marginTop: '30px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
            Volunteer Summary
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px' 
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                {volunteers.filter(v => v.status === 'active').length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Active Volunteers</div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                {volunteers.reduce((total, v) => total + (v.totalHours || 0), 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Total Hours Served</div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1' }}>
                {[...new Set(volunteers.flatMap(v => v.skills || []))].length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Unique Skills</div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>
                {volunteers.filter(v => v.backgroundCheckStatus === 'cleared').length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Background Checks Cleared</div>
            </div>
          </div>
        </div>
      )}

      {/* ========== NEW: SAVE LIST MODAL ========== */}
      {showSaveListModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', color: '#333' }}>
              Save Volunteer List
            </h2>
            
            <p style={{ marginBottom: '20px', color: '#666' }}>
              You're about to save {selectedVolunteerIds.length} volunteer{selectedVolunteerIds.length !== 1 ? 's' : ''} to a list.
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                List Name *
              </label>
              <input
                type="text"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                placeholder="E.g., Youth Ministry Volunteers"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description (Optional)
              </label>
              <textarea
                value={listDescription}
                onChange={(e) => setListDescription(e.target.value)}
                placeholder="What is this list for?"
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSaveListModal(false);
                  setListName('');
                  setListDescription('');
                }}
                disabled={savingList}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: savingList ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsList}
                disabled={savingList || !listName.trim()}
                style={{
                  backgroundColor: savingList || !listName.trim() ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: (savingList || !listName.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {savingList ? 'Saving...' : 'Save List'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ========================================= */}

      {/* Add/Edit Volunteer Modal (same as before, no changes needed) */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {editingVolunteer ? 'Edit Volunteer' : 'Add New Volunteer'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999'
                }}
              >
                ×
              </button>
            </div>

            {/* Basic Information */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Basic Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={volunteerForm.firstName}
                    onChange={(e) => updateVolunteerField('firstName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={volunteerForm.lastName}
                    onChange={(e) => updateVolunteerField('lastName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={volunteerForm.email}
                    onChange={(e) => updateVolunteerField('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={volunteerForm.phone}
                    onChange={(e) => updateVolunteerField('phone', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Address Information
              </h3>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  Street Address
                </label>
                <input
                  type="text"
                  value={volunteerForm.address}
                  onChange={(e) => updateVolunteerField('address', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={volunteerForm.city}
                    onChange={(e) => updateVolunteerField('city', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={volunteerForm.state}
                    onChange={(e) => updateVolunteerField('state', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={volunteerForm.zip}
                    onChange={(e) => updateVolunteerField('zip', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Emergency Contact
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={volunteerForm.emergencyContact}
                    onChange={(e) => updateVolunteerField('emergencyContact', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={volunteerForm.emergencyPhone}
                    onChange={(e) => updateVolunteerField('emergencyPhone', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Skills */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Skills & Abilities
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {skillOptions.map(skill => (
                  <label key={skill} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={volunteerForm.skills.includes(skill)}
                      onChange={() => toggleSkill(skill)}
                    />
                    <span style={{ fontSize: '14px' }}>{skill}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Interests */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Ministry Interests
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {interestOptions.map(interest => (
                  <label key={interest} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={volunteerForm.interests.includes(interest)}
                      onChange={() => toggleInterest(interest)}
                    />
                    <span style={{ fontSize: '14px' }}>{interest}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Status and Background Check */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Status & Background Check
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Volunteer Status
                  </label>
                  <select
                    value={volunteerForm.status}
                    onChange={(e) => updateVolunteerField('status', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                    Background Check Status
                  </label>
                  <select
                    value={volunteerForm.backgroundCheckStatus}
                    onChange={(e) => updateVolunteerField('backgroundCheckStatus', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="pending">Pending</option>
                    <option value="cleared">Cleared</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                Additional Information
              </h3>
              <textarea
                value={volunteerForm.additionalInfo}
                onChange={(e) => updateVolunteerField('additionalInfo', e.target.value)}
                placeholder="Any additional notes about this volunteer..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={saving}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVolunteer}
                disabled={saving || !volunteerForm.firstName || !volunteerForm.lastName || !volunteerForm.email}
                style={{
                  backgroundColor: saving ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: (saving || !volunteerForm.firstName || !volunteerForm.lastName || !volunteerForm.email) ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? (editingVolunteer ? 'Updating...' : 'Adding...') : (editingVolunteer ? 'Update Volunteer' : 'Add Volunteer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerDirectory;